import React, { useLayoutEffect, useRef, useState } from "react";

import type { TicketTemplateConfig, TicketTemplateId } from "../../types/rsvp";
import { TICKET_TEMPLATE_LABELS } from "../../types/rsvp";
import { neighborTicketTemplateId } from "../../utils/ticketTemplateCarousel";
import { PublicTicketTemplateShell } from "./PublicTicketTemplateShell";
import "./ticketTemplate.css";

type PreviewMeta = {
  event_name: string;
  event_date: string;
  city: string;
  artist?: string | null;
  link_kind?: "rsvp" | "guest_list";
  banner_url?: string | null;
  lineup?: { name: string; role?: string | null }[];
};

type Props = {
  templateId: TicketTemplateId;
  config: TicketTemplateConfig;
  meta: PreviewMeta;
  previewPromoters?: { id: string; name: string }[];
  onSelect: (id: TicketTemplateId) => void;
};

const CARD_WIDTH = 392;
const SIDE_PEEK = 96;
const FALLBACK_CARD_H = 740;

export const TicketTemplateCarousel: React.FC<Props> = ({
  templateId,
  config,
  meta,
  previewPromoters = [],
  onSelect,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.72);
  const [cardHeight, setCardHeight] = useState(FALLBACK_CARD_H);

  const prevId = neighborTicketTemplateId(templateId, -1);
  const nextId = neighborTicketTemplateId(templateId, 1);
  const linkKind = meta.link_kind === "guest_list" ? "guest_list" : "rsvp";

  useLayoutEffect(() => {
    const root = rootRef.current;
    const measure = measureRef.current;
    if (!root || !measure) return;

    const update = () => {
      const availH = root.clientHeight;
      const availW = root.clientWidth;
      const cardH = Math.max(measure.scrollHeight, 1);
      const narrow = window.matchMedia("(max-width: 900px)").matches;
      const sideBudget = narrow ? 0 : SIDE_PEEK * 2 + 72;
      const raw = Math.min(0.95, availH / cardH, (availW - sideBudget) / CARD_WIDTH);
      const next = Number.isFinite(raw) && raw > 0 ? Math.max(0.45, Math.round(raw * 1000) / 1000) : 0.72;
      setCardHeight((prev) => (prev === cardH ? prev : cardH));
      setScale((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(root);
    ro.observe(measure);
    return () => ro.disconnect();
  }, [templateId, config, meta, previewPromoters, linkKind]);

  const scaledW = CARD_WIDTH * scale;
  const scaledH = cardHeight * scale;
  const sideScale = scale * 0.88;

  const renderShell = (id: TicketTemplateId) => (
    <PublicTicketTemplateShell
      templateId={id}
      config={config}
      meta={{ ...meta, link_kind: linkKind }}
      eyebrow={linkKind === "guest_list" ? "Guest List" : "RSVP"}
      previewMode
      previewPromoters={previewPromoters}
    />
  );

  const sideSlide = (id: TicketTemplateId, opacity: number) => (
    <div className="ticket-template-carousel__slide" style={{ opacity }}>
      <div
        className="ticket-template-carousel__phone"
        style={{
          width: CARD_WIDTH,
          transform: `scale(${sideScale})`,
          transformOrigin: "top center",
        }}
      >
        {renderShell(id)}
      </div>
      <span className="ticket-template-carousel__label">
        {TICKET_TEMPLATE_LABELS[id]} · {Math.round(opacity * 100)}% opacity
      </span>
    </div>
  );

  return (
    <div ref={rootRef} className="ticket-template-carousel">
      <button
        type="button"
        className="ticket-template-carousel__arrow"
        aria-label="Previous template"
        onClick={() => onSelect(prevId)}
      >
        ←
      </button>
      <div
        className="ticket-template-carousel__track"
        style={
          {
            "--zt-scaled-w": `${scaledW}px`,
            "--zt-scaled-h": `${scaledH}px`,
            "--zt-side-w": `${SIDE_PEEK * scale}px`,
          } as React.CSSProperties
        }
      >
        <button type="button" className="ticket-template-carousel__side" onClick={() => onSelect(prevId)}>
          {sideSlide(prevId, 0.4)}
        </button>
        <div className="ticket-template-carousel__center">
          <div className="ticket-template-carousel__scaler" style={{ width: scaledW, height: scaledH }}>
            <div
              className="ticket-template-carousel__phone ticket-template-carousel__phone--active"
              ref={measureRef}
              style={{
                width: CARD_WIDTH,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              }}
            >
              {renderShell(templateId)}
            </div>
          </div>
        </div>
        <button type="button" className="ticket-template-carousel__side" onClick={() => onSelect(nextId)}>
          {sideSlide(nextId, 0.7)}
        </button>
      </div>
      <button
        type="button"
        className="ticket-template-carousel__arrow"
        aria-label="Next template"
        onClick={() => onSelect(nextId)}
      >
        →
      </button>
    </div>
  );
};
